import { useCallback, useEffect, useMemo, useState } from 'react';

import { areaForCoords } from '../schedule/travel';
import {
  coordsOf,
  DEFAULT_TIMEZONE,
  endMinute,
  eventUrl,
  localDate,
  RSVP_LABEL,
  startMinute,
  venueText,
  zonedIso,
  type Opportunity,
  type OpportunityFormat,
  type RsvpType,
} from '../../shared/opportunity';
import { formatTime, longDate } from '../../shared/taxonomy';
import { useAccountStorage } from '../../shared/account/context';
import { deviceStorage, type KeyValueStorage } from '../../shared/account/storage';

/**
 * A user's own corrections to a listing, from "Edit this event".
 *
 * Only the fields that differ from the listing are stored, so a later fix to
 * the listing still reaches every field the user left alone. Both form buttons
 * store the edit locally and notify the team; "everyone" also asks the team to
 * change the listing. localStorage until accounts are live; see
 * handoff/hidden-and-edited-events.md for the backend version.
 *
 * Fields carry the backend's names. Date and time stay New York wall clock —
 * what the form edits — and become `startsAt`/`endsAt` when an edit is applied.
 */

export interface EventFields {
  name: string;
  dayKey: string;
  /** No published time. Start and end are ignored while this is on. */
  isAllDay: boolean;
  startMinutes: number | null;
  endMinutes: number | null;
  address: string;
  /** Set only when the address came from a picked suggestion. */
  latitude: number | null;
  longitude: number | null;
  format: OpportunityFormat;
  /** The word on the stub, e.g. "PANEL". */
  eventType: string | null;
  rsvpType: RsvpType;
  description: string;
  registrationUrl: string;
}

export type EditScope = 'me' | 'everyone';

/**
 * What is stored. `inPerson`/`online` survive only from an edit made before
 * the move to backend names that changed one checkbox and not the other: that
 * cannot become a `format` without the listing it applies to.
 */
export type StoredFields = Partial<EventFields> & { inPerson?: boolean; online?: boolean };

export interface EventEdit {
  fields: StoredFields;
  /** "everyone" sticks: a later personal tweak doesn't withdraw a request. */
  scope: EditScope;
  editedAt: string;
}

export type EditMap = Record<string, EventEdit>;

const KEY = 'mcw.eventEdits';
const OUTBOX_KEY = 'mcw.editOutbox';

const FIELD_KEYS: (keyof EventFields)[] = [
  'name',
  'dayKey',
  'isAllDay',
  'startMinutes',
  'endMinutes',
  'address',
  'latitude',
  'longitude',
  'format',
  'eventType',
  'rsvpType',
  'description',
  'registrationUrl',
];

export const RSVP_OPTIONS: { value: RsvpType; label: string; hint: string }[] = [
  { value: 'open_to_public', label: RSVP_LABEL.open_to_public, hint: 'Walk in, no registration' },
  { value: 'registration_required', label: RSVP_LABEL.registration_required, hint: 'Anyone can come, but must register first' },
  { value: 'apply_to_attend', label: RSVP_LABEL.apply_to_attend, hint: 'The organizer approves registrations' },
  { value: 'invite_only', label: RSVP_LABEL.invite_only, hint: 'Not open to registration' },
  { value: 'paid', label: RSVP_LABEL.paid, hint: 'A ticket must be bought' },
  { value: 'see_listing', label: RSVP_LABEL.see_listing, hint: "The listing doesn't say" },
];

export const inPersonOf = (format: OpportunityFormat) => format === 'in_person' || format === 'hybrid';
export const onlineOf = (format: OpportunityFormat) => format === 'virtual' || format === 'hybrid';

export function formatOf(inPerson: boolean, online: boolean): OpportunityFormat {
  if (inPerson && online) return 'hybrid';
  if (inPerson) return 'in_person';
  if (online) return 'virtual';
  return 'see_listing';
}

export function fieldsOf(event: Opportunity): EventFields {
  const coords = coordsOf(event);
  return {
    name: event.name,
    dayKey: localDate(event),
    isAllDay: event.isAllDay || startMinute(event) == null,
    startMinutes: startMinute(event),
    endMinutes: endMinute(event),
    address: venueText(event),
    latitude: coords?.[0] ?? null,
    longitude: coords?.[1] ?? null,
    format: event.format,
    eventType: event.eventType,
    rsvpType: event.rsvpType ?? 'see_listing',
    description: event.description || event.summary || '',
    registrationUrl: eventUrl(event),
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The fields of `next` that differ from `base`. */
export function diffFields(base: EventFields, next: EventFields): Partial<EventFields> {
  const out: Partial<EventFields> = {};
  for (const key of FIELD_KEYS) {
    if (!same(base[key], next[key])) (out as Record<string, unknown>)[key] = next[key];
  }
  return out;
}

/** Card text from Markdown: marks and link targets dropped, cut at a word near 280 characters. */
function summarize(markdown: string): string {
  const plain = markdown
    .replace(/\]\([^)]*\)/g, ']')
    .replace(/[*_`#>[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= 280) return plain;
  const cut = plain.slice(0, 280);
  return cut.slice(0, cut.lastIndexOf(' ') > 200 ? cut.lastIndexOf(' ') : 280);
}

/** Resolves a pre-migration half-checkbox edit against the listing it applies to. */
function resolveStored(event: Opportunity, fields: StoredFields): Partial<EventFields> {
  const { inPerson, online, ...rest } = fields;
  if ((inPerson === undefined && online === undefined) || 'format' in rest) return rest;
  return {
    ...rest,
    format: formatOf(inPerson ?? inPersonOf(event.format), online ?? onlineOf(event.format)),
  };
}

export function applyEdit(event: Opportunity, edit: EventEdit): Opportunity {
  const changed = resolveStored(event, edit.fields);
  const f = { ...fieldsOf(event), ...changed };
  const zone = event.timezone ?? DEFAULT_TIMEZONE;

  // Untouched times keep the listing's own instants rather than a round trip
  // through the wall clock.
  let { startsAt, endsAt, isAllDay } = event;
  if ('dayKey' in changed || 'isAllDay' in changed || 'startMinutes' in changed || 'endMinutes' in changed) {
    const timed = !f.isAllDay && f.startMinutes != null;
    isAllDay = !timed;
    if (timed) {
      const start = f.startMinutes as number;
      startsAt = zonedIso(f.dayKey, start, zone);
      // An end nobody published and nobody typed stays an assumption.
      endsAt =
        'endMinutes' in changed || event.endsAt
          ? zonedIso(f.dayKey, f.endMinutes ?? start + 60, zone)
          : null;
    } else {
      startsAt = zonedIso(f.dayKey, 0, zone);
      endsAt = null;
    }
  }

  const venueChanged = 'address' in changed || 'latitude' in changed || 'longitude' in changed;
  const address = f.address.trim();
  const coords: [number, number] | null =
    f.latitude != null && f.longitude != null ? [f.latitude, f.longitude] : null;

  return {
    ...event,
    name: f.name.trim() || event.name,
    registrationUrl: f.registrationUrl,
    startsAt,
    endsAt,
    isAllDay,
    format: f.format,
    eventType: f.eventType,
    rsvpType: f.rsvpType,
    locationType: venueChanged ? (address ? 'address' : 'tbd') : event.locationType,
    address: venueChanged ? address || null : event.address,
    latitude: coords?.[0] ?? null,
    longitude: coords?.[1] ?? null,
    neighborhood: venueChanged ? (coords ? areaForCoords(coords) : null) : event.neighborhood,
    description: f.description,
    summary: 'description' in changed ? summarize(f.description) : event.summary,
  };
}

/** The listings with the user's edits applied. */
export function applyEdits(events: Opportunity[], edits: EditMap): Opportunity[] {
  if (Object.keys(edits).length === 0) return events;
  return events.map((e) => (edits[e.id] ? applyEdit(e, edits[e.id]) : e));
}

// --- what the team is told ---------------------------------------------------

export interface EditChange {
  field: string;
  label: string;
  from: string;
  to: string;
}

const FORMAT_CHANGE_LABEL: Record<OpportunityFormat, string> = {
  in_person: 'In person',
  virtual: 'Online',
  hybrid: 'In person and online',
  see_listing: 'Not specified',
};

/**
 * One readable line per changed thing, from the listing's values to the edited
 * ones. `field` is the /event-edits wire vocabulary, which predates the
 * backend's names and is unchanged.
 */
export function describeChanges(before: EventFields, after: EventFields): EditChange[] {
  const rows: [string, string, (f: EventFields) => string][] = [
    ['title', 'Name', (f) => f.name],
    ['date', 'Date', (f) => `${longDate(f.dayKey).weekday}, ${longDate(f.dayKey).full}`],
    [
      'time',
      'Time',
      (f) =>
        f.isAllDay || f.startMinutes == null
          ? 'No time published'
          : `${formatTime(f.startMinutes)} to ${formatTime(f.endMinutes)}`,
    ],
    [
      'location',
      'Location',
      (f) =>
        f.latitude != null && f.longitude != null
          ? `${f.address} (pinned at ${f.latitude.toFixed(5)}, ${f.longitude.toFixed(5)})`
          : f.address,
    ],
    ['modality', 'In person / online', (f) => FORMAT_CHANGE_LABEL[f.format]],
    ['format', 'Format', (f) => f.eventType ?? 'Not listed'],
    ['rsvp', 'RSVP', (f) => RSVP_LABEL[f.rsvpType]],
    ['description', 'Description', (f) => f.description],
    ['url', 'URL', (f) => f.registrationUrl],
  ];

  return rows
    .map(([field, label, show]) => ({ field, label, from: show(before), to: show(after) }))
    .filter((row) => row.from !== row.to);
}

export interface EditNotice {
  event_id: string;
  event_url: string;
  event_title: string;
  scope: EditScope;
  changes: EditChange[];
  edited_at: string;
}

const REPORTS_URL = import.meta.env.VITE_REPORTS_URL as string | undefined;
const EDITS_URL =
  (import.meta.env.VITE_EVENT_EDITS_URL as string | undefined) ||
  REPORTS_URL?.replace(/\/reports\/?$/, '/event-edits');

/** The edits follow the account; the outbox of notices always stays on the device. */
function readJson<T>(key: string, fallback: T, storage: KeyValueStorage = deviceStorage): T {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown, storage: KeyValueStorage = deviceStorage): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Site data blocked. Edits last this session; notices are sent now or not at all.
  }
}

let flushing = false;

/**
 * Sends queued notices, oldest first.
 *
 * A notice stays queued while offline, rate-limited, or the endpoint isn't
 * deployed yet (404, 5xx), and is retried on the next load or reconnect. A 400
 * is dropped: resending an invalid notice can't fix it.
 */
export async function flushEditOutbox(): Promise<void> {
  if (flushing || !EDITS_URL) return;
  flushing = true;
  try {
    for (const notice of readJson<EditNotice[]>(OUTBOX_KEY, [])) {
      const res = await fetch(EDITS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notice),
      }).catch(() => null);
      if (!res || (!res.ok && res.status !== 400)) break;
      writeJson(
        OUTBOX_KEY,
        readJson<EditNotice[]>(OUTBOX_KEY, []).filter(
          (n) => n.edited_at !== notice.edited_at || n.event_id !== notice.event_id,
        ),
      );
    }
  } finally {
    flushing = false;
  }
}

function queueNotice(notice: EditNotice): void {
  writeJson(OUTBOX_KEY, [...readJson<EditNotice[]>(OUTBOX_KEY, []), notice].slice(-50));
  void flushEditOutbox();
}

// --- the hook ------------------------------------------------------------------

export interface EventEdits {
  edits: EditMap;
  /**
   * Stores `next` as the user's version of `original` and notifies the team.
   * Returns the stored edit, or null when `next` matches the listing again.
   */
  save: (original: Opportunity, next: EventFields, scope: EditScope) => EventEdit | null;
  revert: (id: string) => void;
}

export function useEventEdits(): EventEdits {
  /** localStorage, or the account once the user has one. */
  const storage = useAccountStorage();
  const [edits, setEdits] = useState<EditMap>(() => readJson<EditMap>(KEY, {}, storage));

  // The edits moved: start again from wherever they now live.
  const [source, setSource] = useState(storage);
  if (source !== storage) {
    setSource(storage);
    setEdits(readJson<EditMap>(KEY, {}, storage));
  }

  useEffect(() => writeJson(KEY, edits, storage), [edits, storage]);

  useEffect(() => {
    void flushEditOutbox();
    const retry = () => void flushEditOutbox();
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  const save = useCallback(
    (original: Opportunity, next: EventFields, scope: EditScope): EventEdit | null => {
      const before = fieldsOf(original);
      const fields = diffFields(before, next);
      const editedAt = new Date().toISOString();
      const changes = describeChanges(before, next);

      const unchanged = Object.keys(fields).length === 0;
      setEdits((current) => {
        const rest = { ...current };
        delete rest[original.id];
        if (unchanged) return rest;
        const sticky = current[original.id]?.scope === 'everyone' ? 'everyone' : scope;
        return { ...rest, [original.id]: { fields, scope: sticky, editedAt } };
      });

      if (changes.length > 0) {
        queueNotice({
          event_id: original.id,
          event_url: eventUrl(original),
          event_title: original.name,
          scope,
          changes,
          edited_at: editedAt,
        });
      }

      return unchanged ? null : { fields, scope, editedAt };
    },
    [],
  );

  const revert = useCallback((id: string) => {
    setEdits((current) => {
      if (!current[id]) return current;
      const rest = { ...current };
      delete rest[id];
      return rest;
    });
  }, []);

  return useMemo(() => ({ edits, save, revert }), [edits, save, revert]);
}
